import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/hooks/useAuth';

// Wishlist of product slugs ('3-step-set' for the set). Kept in localStorage
// so it works for guests; for a signed-in customer it is also stored in
// rxk_wishlist_items and merged with the browser's list on sign-in, so it
// follows them to other devices. If that table is missing or a request
// fails, the browser list keeps working on its own.

const KEY = 'rxk-wishlist';
const listeners = new Set<() => void>();
let items: string[] = read();
let owner: string | null = null;

function read(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function write(next: string[]) {
  items = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage blocked: keep the list for this visit */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      items = read();
      l();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

async function syncFromAccount(customerId: string) {
  const { data, error } = await supabase.from('rxk_wishlist_items').select('slug').eq('customer_id', customerId);
  if (error) return;
  const remote = (data as { slug: string }[]).map((r) => r.slug);
  const missing = items.filter((s) => !remote.includes(s));
  if (missing.length) await supabase.from('rxk_wishlist_items').insert(missing.map((slug) => ({ customer_id: customerId, slug })));
  write(Array.from(new Set([...items, ...remote])));
}

export function toggleWishlist(slug: string): boolean {
  const on = !items.includes(slug);
  write(on ? [...items, slug] : items.filter((s) => s !== slug));
  if (owner) {
    const q = supabase.from('rxk_wishlist_items');
    (on ? q.insert({ customer_id: owner, slug }) : q.delete().eq('customer_id', owner).eq('slug', slug)).then(() => {});
  }
  return on;
}

export function useWishlist() {
  const list = useSyncExternalStore(subscribe, () => items);
  const { currentUser } = useAuth();
  const id = currentUser?.id ?? null;
  useEffect(() => {
    if (id === owner) return;
    owner = id;
    if (id) syncFromAccount(id);
  }, [id]);
  return { items: list, has: (slug: string) => list.includes(slug), toggle: toggleWishlist };
}
